import { Box, Group, Menu, Text } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { roleLabelOf } from "~/access/roleLabel";
import { useAccess } from "~/access/useAccess";
import SvgIconRaw from "~/components/SvgIconRaw";
import { useOrgs } from "~/hooks";

import RoundIconButton from "../Button/RoundIconButton";
import SvgIcon from "../SvgIcon";

const SwitchRole = () => {
  const { t } = useTranslation(["common", "access"]);
  const navigate = useNavigate();
  const { role: currentRole, roles, selectRole } = useAccess();

  const { currentOrg } = useOrgs();

  const labelOf = (role: NonNullable<typeof currentRole>) =>
    roleLabelOf(role, currentOrg?.displayName, (key, options) =>
      t(key, { ns: "access", ...options }),
    );

  return (
    <Menu position="bottom-start" offset={8} shadow="md">
      <Menu.Target>
        <Group gap={4} wrap="nowrap" style={{ cursor: "pointer" }}>
          <Text size="xs" fw={600} data-testid="current-role-name">
            {currentRole ? labelOf(currentRole) : ""}
          </Text>
          <Box
            display={{ base: roles.length > 1 ? "flex" : "none", md: "flex" }}
          >
            <RoundIconButton size={16}>
              <SvgIconRaw
                name="ChevronDown"
                size={24}
                color="var(--mantine-color-text-tertiary)"
              />
            </RoundIconButton>
          </Box>
        </Group>
      </Menu.Target>
      <Menu.Dropdown
        miw={200}
        // Same viewport cap as SwitchOrg: Mantine's dropdown has no
        // max-height, so a long role list otherwise runs off-screen.
        mah="calc(100vh - 96px)"
        style={{ overflowY: "auto" }}
      >
        {roles.length > 1 &&
          roles.map((role) => {
            const isCurrent = currentRole?.id === role.id;
            return (
              <Menu.Item
                key={"user_role_" + role.id}
                onClick={() => selectRole(role.id)}
                disabled={isCurrent}
                aria-disabled={isCurrent || undefined}
                leftSection={
                  isCurrent ? (
                    <SvgIconRaw
                      name="Check"
                      size={20}
                      data-testid="CheckIcon"
                    />
                  ) : (
                    <Box w={20} />
                  )
                }
              >
                <Text size="xs" fw={600}>
                  {labelOf(role)}
                </Text>
              </Menu.Item>
            );
          })}
        {roles.length > 1 && <Menu.Divider />}
        <Menu.Item
          onClick={() => navigate({ to: "/logout" })}
          leftSection={<SvgIcon name="Logout" size={20} />}
        >
          <Text size="xs" fw={600}>
            {t("nav.logout")}
          </Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default SwitchRole;
